import type { ModelName } from "./dataTypes/ContentType";
import type { Node } from "./nodeTypes";

//
// Structured node validation.
//
// Each rule yields a NodeIssue instead of a bare boolean so the graph can both
// color nodes (validateNode) and list every problem in the issues panel with a
// human-readable message. Severities:
// ➡︎ "error"   = the experience is broken at runtime (missing media, quiz that
//               can never be answered correctly). Turns the node invalid.
// ➡︎ "warning" = quality problem (empty question/text). Listed in the issues
//               panel but the node stays valid.
//

export type GraphIssueSeverity = "error" | "warning";

export type NodeIssue = {
  severity: GraphIssueSeverity;
  // i18n suffix, resolved under pages.project.edit.graphIssue.*
  messageKey: string;
  // interpolation values for the i18n message (e.g. { language: "Français" })
  messageParams?: Record<string, string | number>;
  // content field names behind the issue (untranslatedFields) — resolved to
  // the edit-form labels via prettyLabel when displayed
  fieldKeys?: string[];
};

const hasText = (value: unknown): boolean => typeof value === "string" && value.trim().length > 0;

const hasId = (value: unknown): boolean => Number(value) >= 1;

// Booleans can come back as strings depending on the transport; be defensive
// so a "false" string never counts as a checked answer.
const isTrue = (value: unknown): boolean => value === true || value === "true" || value === 1;

// Fields like hotspot_info / keywords_info hold a JSON-encoded array (or a raw
// array). "No items" = null, blank string or empty array; any unknown shape is
// treated as filled so we never cry wolf on data we don't understand.
const hasItems = (raw: unknown): boolean => {
  if (raw == null) return false;
  if (Array.isArray(raw)) return raw.length > 0;
  if (typeof raw !== "string") return true;
  const s = raw.trim();
  if (!s) return false;
  try {
    const parsed = JSON.parse(s);
    return Array.isArray(parsed) ? parsed.length > 0 : true;
  } catch {
    return true;
  }
};

export const collectNodeIssues = (node: Node): NodeIssue[] => {
  const issues: NodeIssue[] = [];
  const details: any = node.details ?? {};
  const error = (messageKey: string) => issues.push({ severity: "error", messageKey });
  const warning = (messageKey: string) => issues.push({ severity: "warning", messageKey });

  if (!hasText(details.name)) error("missingName");

  switch (node.modelName as ModelName) {
    case "content-scene":
      if (!hasId(details.source_media)) error("missingMedia");
      break;
    case "content-audio":
    case "content-avatar-audio-player":
      if (!hasId(details.source_audio)) error("missingMedia");
      break;
    // NB: onboarding/thumbnail texts are deliberately NOT checked — they are
    // optional captions (the Unity validator skips them too) and flagging them
    // buried real problems under dozens of false warnings on live projects.
    case "content-onboarding":
      if (!hasId(details.source_image_2d)) error("missingMedia");
      break;
    case "content-thumbnail":
      if (!hasId(details.source_image)) error("missingMedia");
      break;
    case "content-tuto":
      if (!hasId(details.source_logo)) error("missingMedia");
      break;
    case "content-video-2-d":
      if (!hasId(details.source_media)) error("missingMedia");
      break;
    case "content-pdf-viewer":
      if (!hasId(details.source_pdf)) error("missingMedia");
      break;
    case "content-3d-viewer":
    case "content-3-d-viewer":
      if (!hasId(details.source_model3d)) error("missingMedia");
      break;
    case "content-quizz-1": {
      const marked = [1, 2, 3].filter((i) => isTrue(details[`is_answer_${i}_true`]));
      if (marked.length === 0) error("quizNoCorrectAnswer");
      // A correct answer with no text can never be picked knowingly: broken.
      if (marked.some((i) => !hasText(details[`answer_${i}_text`]))) error("emptyCorrectAnswer");
      if (!hasText(details.question_text)) warning("emptyQuestion");
      break;
    }
    case "content-quizz-2": {
      const marked = [1, 2, 3].filter((i) => isTrue(details[`is_answer_${i}_true`]));
      if (marked.length === 0) error("quizNoCorrectAnswer");
      if (marked.some((i) => !hasId(details[`answer_${i}_image`]))) error("missingAnswerImage");
      if (!hasText(details.question_text)) warning("emptyQuestion");
      break;
    }
    case "content-quizz-3":
    case "content-yes-no-question":
      if (!hasText(details.question_text)) warning("emptyQuestion");
      break;
    case "content-quizz-sort":
      if (!hasText(details.question)) warning("emptyQuestion");
      break;
    case "content-text":
    case "content-hotspot-scene-image":
    case "content-hotspot-scene-video":
      if (!hasText(details.text)) warning("emptyText");
      break;
    // NB: content-danger-catch has NO instruction field (only
    // text_success/text_fail, both optional) — pairing it with photo-catch
    // here made every danger-catch warn "empty instruction" forever.
    case "content-photo-catch":
      if (!hasText(details.text_instruction)) warning("emptyInstruction");
      break;
    case "content-zone-detect":
    case "content-vr-locator":
      if (!hasItems(details.hotspot_info)) warning("noHotspots");
      break;
    case "content-speech-to-check":
      if (!hasItems(details.keywords_info)) warning("noKeywords");
      break;
    default:
      break;
  }

  return issues;
};

export const validateNode = (node: Node): boolean =>
  collectNodeIssues(node).every((issue) => issue.severity !== "error");

// Scoring-scene is editable on this branch (node-form-edit-all-fields); the
// rewrite's universal missingName check subsumes the old scoring-scene name
// validator, so no node needs to be blocked from editing.
export const isNodeEditable = (_node: Node) => true;
